<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import KanbanCard from './KanbanCard.vue';
import KanbanAddConversationDialog from './KanbanAddConversationDialog.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  stage: {
    type: Object,
    required: true,
  },
  conversations: {
    type: Array,
    default: () => [],
  },
  isLoadingMore: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'cardDropped',
  'loadMore',
  'openConversation',
  'editStage',
]);

const { t } = useI18n();

const localConversations = ref([...props.conversations]);

watch(
  () => props.conversations,
  value => {
    localConversations.value = [...value];
  }
);

const formattedTotal = computed(() =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(props.stage.total_value || 0)
);

const hasMore = computed(
  () => props.conversations.length < (props.stage.conversations_count || 0)
);

const onChange = change => {
  if (!change.added) return;
  emit('cardDropped', {
    conversation: change.added.element,
    toStageId: props.stage.id,
  });
};

const addConversationDialogRef = ref(null);
const openAddConversationDialog = () =>
  addConversationDialogRef.value?.open(props.stage);
</script>

<template>
  <div
    class="flex flex-col shrink-0 w-72 max-h-full bg-n-background rounded-xl border border-n-weak"
  >
    <div
      class="flex items-center justify-between gap-2 p-3 border-b border-n-weak"
    >
      <div class="flex items-center gap-2 min-w-0">
        <span
          class="w-2.5 h-2.5 rounded-full shrink-0"
          :style="{ backgroundColor: stage.color }"
        />
        <span class="text-sm font-medium truncate text-n-slate-12">
          {{ stage.name }}
        </span>
        <span class="text-xs text-n-slate-11 shrink-0">
          {{ stage.conversations_count || 0 }}
        </span>
      </div>
      <button
        class="text-xs text-n-slate-11 hover:text-n-slate-12 shrink-0"
        :title="t('KANBAN.BOARD.SETTINGS')"
        @click="emit('editStage', stage)"
      >
        <i class="i-lucide-pencil" />
      </button>
    </div>
    <div class="px-3 py-1.5 text-xs font-medium text-n-slate-11">
      {{ formattedTotal }}
    </div>
    <Draggable
      v-model="localConversations"
      :group="{ name: 'kanban-cards' }"
      item-key="id"
      class="flex flex-col gap-2 p-3 pt-1 overflow-y-auto grow min-h-24"
      @change="onChange"
    >
      <template #item="{ element }">
        <div @click="emit('openConversation', element)">
          <KanbanCard :conversation="element" />
        </div>
      </template>
    </Draggable>
    <button
      v-if="hasMore"
      type="button"
      class="flex items-center justify-center gap-1.5 mx-3 mb-2 py-1.5 text-xs font-medium rounded-lg text-n-slate-11 hover:bg-n-solid-3 disabled:opacity-60"
      :disabled="isLoadingMore"
      @click="emit('loadMore')"
    >
      <Spinner v-if="isLoadingMore" :size="12" />
      {{ t('KANBAN.BOARD.LOAD_MORE') }}
    </button>
    <button
      type="button"
      class="flex items-center justify-center gap-1.5 mx-3 mb-3 py-2 text-sm font-medium rounded-lg border border-dashed border-n-weak text-n-slate-11 hover:border-n-blue-9 hover:text-n-blue-9 hover:bg-n-blue-2"
      @click="openAddConversationDialog"
    >
      <i class="i-lucide-plus" />
      {{ t('KANBAN.BOARD.ADD_CONVERSATION.TITLE') }}
    </button>
    <KanbanAddConversationDialog ref="addConversationDialogRef" />
  </div>
</template>
